/**
 * RegionConfig — per-Conscierra-domain configuration for multi-domain Vite SPA.
 *
 * Source of truth for hostname → region resolution and per-region content
 * (legal entity, address, phone, email, hours, currency, locale, compliance refs).
 *
 * Per F1-14A § 15.1 (multi-domain architecture) + code-handoff-fy27-website-2026-05-09.md
 * § 12.3 canonical shape. Consumed via the `useRegion()` hook.
 *
 * Hostname-to-region mapping:
 *   conscierra.au    → AU  (full content)
 *   conscierra.sg    → SG  (TBD: operator-supplied)
 *   conscierra.id    → ID  (TBD: operator-supplied + Bahasa Indonesia)
 *   conscierra.fj    → FJ  (TBD: operator-supplied)
 *   any other host   → AU  (default fallback per BR-C-F14A-030)
 *
 * Build-time override: `VITE_HOST=sg pnpm build` selects SG at SSG time.
 * Runtime detection (dev / preview): `window.location.host`.
 *
 * Placeholders carry ADR-0035 markers: until operator content lands, those domains
 * render `{TBD: ...}` strings visibly. Per `_project/stub-acceptance.md`, this is
 * authorised stub content for non-AU domains (operator confirmed "go" at scope-
 * envelope checklist 2026-05-13; partner-supplied content per OQ-7/8/9/11/12).
 */

export type RegionCode = 'au' | 'sg' | 'id' | 'fj';

export interface RegionConfig {
  readonly code: RegionCode;
  readonly host: string; // canonical hostname (e.g. 'conscierra.au')
  readonly displayName: string;
  readonly legalEntity: string;
  readonly address: {
    readonly lines: readonly string[]; // multi-line address (city, country last line)
  };
  readonly phone: { readonly display: string; readonly tel: string }; // tel: format for href
  readonly email: string;
  readonly bookingUrl: string; // Microsoft Bookings discovery-call page — embedded on /contact (AC-11)
  readonly hours: string; // free-text — "Mon–Fri 8:30am–5pm AEST"
  readonly currency: 'AUD' | 'SGD' | 'IDR' | 'FJD';
  readonly locale: string; // BCP-47 — drives Intl.NumberFormat / Intl.DateTimeFormat
  readonly languages: readonly ('en' | 'id')[]; // ID gets ['en', 'id'] for the toggle
  readonly defaultLanguage: 'en' | 'id';
  readonly footerTagline: string; // "A tech partner that cares..." region-specific framing
  readonly complianceBadge: string; // e.g. "ISO 9001 · ISO 27001 · ISO/IEC 42001 aligned"
  readonly privacyJurisdiction: 'APP' | 'PDPA' | 'PDPL' | 'FJ-PA';
}

/**
 * AU — fully populated; matches existing pre-multi-domain conscierra.au content
 * exactly so behavioural change on AU is zero.
 */
const AU: RegionConfig = {
  code: 'au',
  host: 'conscierra.au',
  displayName: 'Conscierra — Australia',
  legalEntity: 'B & A Technologies Pty Ltd',
  address: {
    lines: ['Suite 13–14, 165 Moggill Rd', 'Taringa QLD 4068', 'Australia'],
  },
  phone: { display: '+61 7 4602 0583', tel: '+61746020583' },
  email: 'info@conscierra.au',
  bookingUrl: 'https://outlook.office.com/book/Discovery@conscierra.au/?ismsaljsauthenabled',
  hours: 'Mon–Fri 8:30am–5pm AEST',
  currency: 'AUD',
  locale: 'en-AU',
  languages: ['en'],
  defaultLanguage: 'en',
  footerTagline:
    'A tech partner that cares. From our base across Australia, Singapore and Indonesia, Conscierra delivers Managed IT, Platform Builds, AI Enablement, and Marketing Technology to ambitious businesses worldwide — backed by ISO 9001 + 27001 certified processes.',
  complianceBadge: 'ISO 9001 · ISO 27001 · ISO/IEC 42001 aligned delivery framework.',
  privacyJurisdiction: 'APP',
};

/**
 * SG — operator content pending (OQ-7/11/12). Placeholder strings visible until
 * Stephen + partner supply finalised content. Pricing/compliance refs per
 * fy27-asean-regional-expansion-2026-05-09.md v1.3 § 4.
 */
const SG: RegionConfig = {
  code: 'sg',
  host: 'conscierra.sg',
  displayName: 'Conscierra — Singapore',
  legalEntity: '{TBD: SG legal entity name — Stephen + partner finalisation}',
  address: {
    lines: ['{TBD: SG office address}', 'Singapore'],
  },
  phone: { display: '{TBD: SG phone}', tel: '' },
  email: 'info@conscierra.sg',
  // Shared Conscierra Bookings page across regions until per-region calendars exist.
  bookingUrl: 'https://outlook.office.com/book/Discovery@conscierra.au/?ismsaljsauthenabled',
  hours: 'Mon–Fri 9:00am–6:00pm SGT',
  currency: 'SGD',
  locale: 'en-SG',
  languages: ['en'],
  defaultLanguage: 'en',
  footerTagline:
    'A tech partner that cares. Conscierra delivers Managed IT and Platform Builds to Singapore SMB and mid-market businesses — aligned with PDPA, Cybersecurity Act 2018, and MAS TRM expectations.',
  complianceBadge: 'ISO 9001 · ISO 27001 · ISO/IEC 42001 aligned delivery framework.',
  privacyJurisdiction: 'PDPA',
};

/**
 * ID — bilingual (EN + Bahasa Indonesia). Translation infra ships separately
 * (Feature #7865); this config declares the language axis. Operator content
 * pending (OQ-7/9/11/12) — partner-finalised legal entity + Indonesian
 * translations to follow.
 */
const ID: RegionConfig = {
  code: 'id',
  host: 'conscierra.id',
  displayName: 'Conscierra — Indonesia',
  legalEntity: '{TBD: ID partner legal entity name — partner finalisation}',
  address: {
    lines: ['{TBD: ID office address}', 'Indonesia'],
  },
  phone: { display: '{TBD: ID phone}', tel: '' },
  email: 'info@conscierra.id',
  // Shared Conscierra Bookings page across regions until per-region calendars exist.
  bookingUrl: 'https://outlook.office.com/book/Discovery@conscierra.au/?ismsaljsauthenabled',
  hours: 'Mon–Fri 9:00am–6:00pm WIB',
  currency: 'IDR',
  locale: 'id-ID',
  languages: ['en', 'id'],
  defaultLanguage: 'en', // default EN across all 4 domains per F1-14A § 15.2
  footerTagline:
    '{TODO(translation-pending): Indonesian-partner-translated footer tagline. EN fallback in use.}',
  complianceBadge: 'ISO 9001 · ISO 27001 · ISO/IEC 42001 aligned delivery framework.',
  privacyJurisdiction: 'PDPL',
};

/**
 * FJ — operator content pending (OQ-8/11/12). Placeholder strings visible until
 * Stephen + partner finalisation. Compliance ref: Fijian Data Protection Bill
 * 2024 + RBF.
 */
const FJ: RegionConfig = {
  code: 'fj',
  host: 'conscierra.fj',
  displayName: 'Conscierra — Fiji',
  legalEntity: '{TBD: FJ partner legal entity name — partner finalisation}',
  address: {
    lines: ['{TBD: FJ office address}', 'Fiji'],
  },
  phone: { display: '{TBD: FJ phone}', tel: '' },
  email: 'info@conscierra.fj',
  // Shared Conscierra Bookings page across regions until per-region calendars exist.
  bookingUrl: 'https://outlook.office.com/book/Discovery@conscierra.au/?ismsaljsauthenabled',
  hours: 'Mon–Fri 8:30am–5pm FJT',
  currency: 'FJD',
  locale: 'en-FJ',
  languages: ['en'],
  defaultLanguage: 'en',
  footerTagline:
    'A tech partner that cares. Conscierra delivers Managed IT and Platform Builds to Fijian SMB and mid-market businesses — aligned with the Fijian Data Protection Bill 2024 and RBF expectations.',
  complianceBadge: 'ISO 9001 · ISO 27001 · ISO/IEC 42001 aligned delivery framework.',
  privacyJurisdiction: 'FJ-PA',
};

const REGIONS: Record<RegionCode, RegionConfig> = { au: AU, sg: SG, id: ID, fj: FJ };

/**
 * Hostname → region resolution.
 *
 * Per BR-C-F14A-030: hostname matches one of the canonical hosts → that region;
 * any unrecognised host (localhost, App Service default URL, ngrok, etc.) falls
 * back to AU. This keeps dev + preview workflows on AU content unless the
 * developer explicitly sets `VITE_HOST` at build/dev time.
 */
export function resolveRegion(host: string | undefined | null): RegionConfig {
  if (!host) return AU;
  // Strip port (`conscierra.au:5120` → `conscierra.au`).
  const bare = host.split(':')[0].toLowerCase();
  if (bare === 'conscierra.sg' || bare.endsWith('.conscierra.sg')) return SG;
  if (bare === 'conscierra.id' || bare.endsWith('.conscierra.id')) return ID;
  if (bare === 'conscierra.fj' || bare.endsWith('.conscierra.fj')) return FJ;
  // AU is the default — including conscierra.au, the App Service default URL,
  // localhost, and any unrecognised host.
  return AU;
}

/**
 * Build-time region resolution: prefer the `VITE_HOST` env var set during
 * `pnpm build` (e.g. `VITE_HOST=sg pnpm build`); fall back to AU. Used by the
 * static prerender pass so each `dist/{region}/` output bakes the right
 * content into HTML.
 */
export function resolveBuildRegion(): RegionConfig {
  // import.meta.env is injected by Vite at build time; in non-Vite test
  // environments (Node, vitest jsdom) the var simply isn't present and we
  // fall back to AU. That's correct — tests run against AU defaults.
  const code = (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_HOST as
    | RegionCode
    | undefined;
  if (code && REGIONS[code]) return REGIONS[code];
  return AU;
}

export const ALL_REGIONS: readonly RegionConfig[] = [AU, SG, ID, FJ];
export const AU_DEFAULT = AU;
